import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Image, 
  HelpCircle, 
  Star, 
  User, 
  Code, 
  Settings,
  LogOut,
  Menu,
  X,
  BarChart3
} from 'lucide-react';
import { getMe } from '../lib/api';

const sidebarLinks = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/packages', label: 'Packages', icon: Package },
  { path: '/admin/media', label: 'Media', icon: Image },
  { path: '/admin/faqs', label: 'FAQs', icon: HelpCircle },
  { path: '/admin/reviews', label: 'Reviews', icon: Star },
  { path: '/admin/about', label: 'About', icon: User },
  { path: '/admin/widgets', label: 'Widgets', icon: Code },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

export const AdminLayout = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin');
        return;
      }
      try {
        const res = await getMe();
        setAdmin(res.data);
      } catch (error) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const isActive = (path) => location.pathname === path;

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-neutral-200 z-40 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-mobile-toggle">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <span className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Admin Panel</span>
        <div className="w-6" />
      </div>

      {/* Sidebar */}
      <aside 
        className={`admin-sidebar pt-8 transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } z-50`}
        data-testid="admin-sidebar"
      >
        <div className="px-6 mb-8">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
            Admin Panel
          </h1>
          <p className="text-sm text-neutral-500 mt-1">{admin.email}</p>
        </div>

        <nav className="flex flex-col gap-1">
          {sidebarLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setSidebarOpen(false)}
                className={`admin-nav-item ${isActive(link.path) ? 'admin-nav-item-active' : ''}`}
                data-testid={`admin-nav-${link.path.split('/').pop()}`}
              >
                <Icon size={20} />
                <span>{link.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-8 left-0 right-0 px-2">
          <button
            onClick={handleLogout}
            className="admin-nav-item w-full text-red-600 hover:bg-red-50"
            data-testid="admin-logout-btn"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
          <Link
            to="/"
            className="admin-nav-item w-full mt-2"
            data-testid="admin-view-site-btn"
          >
            <span>View Site →</span>
          </Link>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
